import { PageRoutes } from "@/app/components/router/routes";
import { FileType } from "../../constants/misc/FileType";
import useExportToFile from "../../utils/useExportToFile";
import { apiInstance } from "../apiInstance";
import { ApiRoutes } from "../apiRoutes";
import { brGet, brPost } from "../brRequestWrapper";
import { useQuery } from "react-query";
import { BillingReminderSearchFiltersResponse, InvoicesAllResponse } from "@/app/features/InvoiceBillingReminder/billingReminder.types";
import { FilterInfo, SortInfo } from "./SearchInvoices";

export interface BillingReminderSearchRequest {
  page: number;
  pageSize: number;
  filters?: Array<{ column: string; value: string }>;
  sort?: { field: string; order: 'asc' | 'desc' };
}

const AGE_BUCKETS: Array<{ label: string; min: number; max?: number }> = [
  { label: '1-30 Days',  min: 1,  max: 30 },
  { label: '31-45 Days', min: 31, max: 45 },
  { label: '46-60 Days', min: 46, max: 60 },
  { label: '61-90 Days', min: 61, max: 90 },
  { label: '91+ Days',   min: 91 },
];

export const AGE_BUCKET_LABELS: string[] = AGE_BUCKETS.map((b) => b.label);

function buildAgeBucketFilter(bucketLabels: string[]): FilterInfo | undefined {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const bucketClauses: FilterInfo[] = bucketLabels.flatMap((label) => {
    const bucket = AGE_BUCKETS.find((b) => b.label === label);
    if (!bucket) return [];

    const maxInvoiceDate = new Date(today);
    maxInvoiceDate.setDate(today.getDate() - (bucket.min - 2)); // exclusive upper bound: start of day after boundary

    const pair: FilterInfo[] = [{ field: 'InvoiceDate', filterType: 'lt', value: maxInvoiceDate.toISOString() }];

    if (bucket.max !== undefined) {
      const minInvoiceDate = new Date(today);
      minInvoiceDate.setDate(today.getDate() - (bucket.max - 1));
      pair.push({ field: 'InvoiceDate', filterType: 'gte', value: minInvoiceDate.toISOString() });
    }

    if (pair.length === 1) return pair;
    return [{ condition: 'and', filter: pair }];
  });

  if (bucketClauses.length === 0) return undefined;
  if (bucketClauses.length === 1) return bucketClauses[0];
  return { condition: 'or', filter: bucketClauses };
}

/** Maps frontend column names to BillingReminderInvoice entity property names. */
const COLUMN_MAP: Record<string, string> = {
  carrierName: 'CarrierName',
  status: 'CollectionStatus',
  invoiceNumber: 'InvoiceNumber',
  amount: 'InvoiceAmount',
  invoiceDate: 'InvoiceDate',
  invoiceDueDate: 'DueDate',
  age: 'InvoiceDate',
};

function buildDynamicQuery(request: BillingReminderSearchRequest) {
  const filter: FilterInfo | undefined = (() => {
    if (!request.filters?.length) return undefined;
    const groups = request.filters.reduce<Record<string, string[]>>((acc, f) => {
      (acc[f.column] ??= []).push(f.value);
      return acc;
    }, {});

    const clauses: FilterInfo[] = Object.entries(groups).flatMap(([col, vals]) => {
      if (col === 'age') {
        const ageFilter = buildAgeBucketFilter(vals);
        return ageFilter ? [ageFilter] : [];
      }
      return [{ field: COLUMN_MAP[col] ?? col, filterType: 'in', value: vals.join(',') }];
    });

    if (clauses.length === 0) return undefined;
    return clauses.length === 1
      ? clauses[0]
      : { condition: 'and', filter: clauses };
  })();

  const sort: SortInfo[] | undefined = request.sort
    ? [{ field: COLUMN_MAP[request.sort.field] ?? request.sort.field, order: request.sort.order }]
    : undefined;

  return {
    pagination: { page: request.page, size: request.pageSize, includeTotalCount: true },
    filter,
    sort,
  };
}

const { exportFile } = useExportToFile();

export const getInvoicesAllCSV = async () => {
  const requestConfig = {
    headers: {
      "api-version": "2.0",
    },
  };
  const path = ApiRoutes.AllInvoices;
  const request = `${path}?iscsv=${true}`;
  const response = await apiInstance.get(request, requestConfig);

  await exportFile("BillingReminder", response.data, FileType.Csv);
  return response.data;
};

export const useSearchBillingRemindersV2 = (request: BillingReminderSearchRequest, enabled = true) =>
  useQuery({
    queryKey: [
      'billing-reminders-v2',
      request.page,
      request.pageSize,
      request.filters,
      request.sort,
    ],
    enabled,
    keepPreviousData: true,
    queryFn: async (): Promise<InvoicesAllResponse> => {
      try {
        const response = await brPost<InvoicesAllResponse>(
          ApiRoutes.AllInvoicesSearch,
          buildDynamicQuery(request),
          { headers: { 'api-version': '2.0' } }
        );
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 400) {
          return { items: [], pageSize: request.pageSize, totalCount: 0, hasMore: false };
        }
        window.location.replace(PageRoutes.AUTH_ERROR);
        throw error;
      }
    },
  });

export const useGetBillingReminderFilters = (enabled = true) =>
  useQuery({
    queryKey: ['billing-reminders-filters'],
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<BillingReminderSearchFiltersResponse> => {
      try {
        const response = await brGet<BillingReminderSearchFiltersResponse>(
          ApiRoutes.AllInvoicesSearchFilters,
          { headers: { 'api-version': '2.0' } },
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.status === 400) {
          return { carrierNames: [], collectionStatuses: [] };
        }
        window.location.replace(PageRoutes.AUTH_ERROR);
        throw error;
      }
    },
  });
